import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = 'https://chatpanel.my.id/api';

const WidgetSettings = () => {
  const [settings, setSettings] = useState({
    greeting_text: '',
    bubble_text: 'Chat with us 👋',
    greeting_message: 'Hi! How can I help you today?',
    floating_icon_url: null,
    welcome_title: '',
    welcome_subtitle: '',
    name_button_text: '',
    quick_chat_text: '',
    quick_chat_subtitle: '',
    icon_url: null,
    avatar_url: null,
    primary_color: '#667eea',
    secondary_color: '#764ba2',
    position: 'bottom-right',
    form_title: 'Please introduce yourself:',
    form_email_placeholder: 'Email address',
    form_name_placeholder: 'Your name',
    form_newsletter_text: 'Sign up for our newsletter',
    form_submit_button: 'Send'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [floatingIconFile, setFloatingIconFile] = useState(null);
  const [floatingIconPreview, setFloatingIconPreview] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/widget-settings`);
      if (response.data.success) {
        setSettings(response.data.data);
        if (response.data.data.icon_url) {
          setIconPreview(response.data.data.icon_url);
        }
        if (response.data.data.avatar_url) {
          setAvatarPreview(response.data.data.avatar_url);
        }
        if (response.data.data.floating_icon_url) {
          setFloatingIconPreview(response.data.data.floating_icon_url);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showMessage('error', 'Failed to load settings');
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIconFile(file);
      setIconPreview(URL.createObjectURL(file));
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleFloatingIconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFloatingIconFile(file);
      setFloatingIconPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      Object.keys(settings).forEach(key => {
        if (settings[key] !== null && key !== 'icon_url' && key !== 'avatar_url' && key !== 'floating_icon_url') {
          formData.append(key, settings[key]);
        }
      });

      if (iconFile) {
        formData.append('icon', iconFile);
      }

      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      if (floatingIconFile) {
        formData.append('floating_icon', floatingIconFile);
      }

      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_BASE}/widget-settings`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        showMessage('success', 'Settings saved successfully');
        fetchSettings();
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showMessage('error', 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '30px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span>🎨</span> Widget Settings
      </h2>

      {message.text && (
        <div style={{
          padding: '12px 20px',
          marginBottom: '20px',
          borderRadius: '8px',
          backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
          {/* Left Column - Configuration */}
          <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '25px' }}>Widget Configuration</h3>

            {/* Widget Active Toggle */}
            <div style={{ marginBottom: '30px', padding: '20px', background: '#f0f9ff', borderRadius: '8px', border: '2px solid #3b82f6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '5px', color: '#1e40af' }}>Widget Status</h4>
                  <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
                    {settings.is_active ? '✅ Widget is currently ACTIVE' : '⛔ Widget is currently INACTIVE'}
                  </p>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: '60px', height: '34px' }}>
                  <input
                    type="checkbox"
                    checked={settings.is_active === 1}
                    onChange={(e) => handleInputChange({ target: { name: 'is_active', value: e.target.checked ? 1 : 0 } })}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: settings.is_active === 1 ? '#22c55e' : '#cbd5e1',
                    transition: '0.4s',
                    borderRadius: '34px'
                  }}>
                    <span style={{
                      position: 'absolute',
                      content: '""',
                      height: '26px',
                      width: '26px',
                      left: settings.is_active === 1 ? '30px' : '4px',
                      bottom: '4px',
                      backgroundColor: 'white',
                      transition: '0.4s',
                      borderRadius: '50%'
                    }}></span>
                  </span>
                </label>
              </div>
            </div>


            {/* Welcome Screen Section */}
            <div style={{ marginBottom: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px', color: '#667eea' }}>Welcome Screen</h4>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Welcome Title
                </label>
                <input
                  type="text"
                  name="welcome_title"
                  value={settings.welcome_title}
                  onChange={handleInputChange}
                  placeholder="Hi there 👋"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.3s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Welcome Subtitle
                </label>
                <input
                  type="text"
                  name="welcome_subtitle"
                  value={settings.welcome_subtitle}
                  onChange={handleInputChange}
                  placeholder="Welcome to our website. Ask us anything 🎉"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.3s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Name Button Text
                </label>
                <input
                  type="text"
                  name="name_button_text"
                  value={settings.name_button_text}
                  onChange={handleInputChange}
                  placeholder="Sebutkan nama kamu"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.3s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Quick Chat Text
                </label>
                <input
                  type="text"
                  name="quick_chat_text"
                  value={settings.quick_chat_text}
                  onChange={handleInputChange}
                  placeholder="Chat with us"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.3s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              <div style={{ marginBottom: '0' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Quick Chat Subtitle
                </label>
                <input
                  type="text"
                  name="quick_chat_subtitle"
                  value={settings.quick_chat_subtitle}
                  onChange={handleInputChange}
                  placeholder="We reply immediately"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.3s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>
            </div>

            {/* Greeting Bubble Text */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                Greeting Bubble Text
              </label>
              <input
                type="text"
                name="greeting_text"
                value={settings.greeting_text}
                onChange={handleInputChange}
                placeholder="Halo! Ada yang bisa kami bantu? 😊"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.3s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />

            {/* Floating Bubble Text */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                Floating Bubble Text
                <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                  (Text yang muncul di sebelah button widget)
                </span>
              </label>
              <input
                type="text"
                name="bubble_text"
                value={settings.bubble_text}
                onChange={handleInputChange}
                placeholder="Chat with us 👋"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.3s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>

            {/* Chat Greeting Message */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                Chat Greeting Message
              </label>
              <input
                type="text"
                name="greeting_message"
                value={settings.greeting_message}
                onChange={handleInputChange}
                placeholder="Hi! How can I help you today?"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.3s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>
            </div>

            {/* Colors */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Primary Color
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="color"
                    name="primary_color"
                    value={settings.primary_color}
                    onChange={handleInputChange}
                    style={{ width: '60px', height: '45px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={settings.primary_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Secondary Color
                </label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="color"
                    name="secondary_color"
                    value={settings.secondary_color}
                    onChange={handleInputChange}
                    style={{ width: '60px', height: '45px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={settings.secondary_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Widget Position */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                Widget Position
              </label>
              <select
                name="position"
                value={settings.position}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer',
                  backgroundColor: 'white'
                }}
              >
                <option value="bottom-right">Bottom Right</option>
                <option value="bottom-left">Bottom Left</option>
                <option value="top-right">Top Right</option>
                <option value="top-left">Top Left</option>
              </select>
            </div>

            {/* Widget Icon */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                Widget Icon (Header)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleIconChange}
                style={{ width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
              />
              {iconPreview && (
                <img src={iconPreview} alt="Icon preview" style={{ marginTop: '10px', width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #e0e0e0' }} />
              )}
            </div>

            {/* Floating Chat Icon - NEW FIELD */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                Floating Chat Icon (Bottom Right Button)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFloatingIconChange}
                style={{ width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
              />
              {floatingIconPreview && (
                <img src={floatingIconPreview} alt="Floating Icon preview" style={{ marginTop: '10px', width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #e0e0e0' }} />
              )}
            </div>

            {/* Avatar */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                Chat Avatar (Profile Photo)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
              />
              {avatarPreview && (
                <img src={avatarPreview} alt="Avatar preview" style={{ marginTop: '10px', width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #e0e0e0' }} />
              )}
            </div>


            {/* User Form Customization */}
            <div style={{ marginTop: '30px', paddingTop: '30px', borderTop: '2px solid #e0e0e0' }}>
              <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>User Form Customization</h3>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Form Title
                </label>
                <input
                  type="text"
                  name="form_title"
                  value={settings.form_title}
                  onChange={handleInputChange}
                  placeholder="Please introduce yourself:"
                  style={{ width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Email Input Placeholder
                </label>
                <input
                  type="text"
                  name="form_email_placeholder"
                  value={settings.form_email_placeholder}
                  onChange={handleInputChange}
                  placeholder="Email address"
                  style={{ width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Name Input Placeholder
                </label>
                <input
                  type="text"
                  name="form_name_placeholder"
                  value={settings.form_name_placeholder}
                  onChange={handleInputChange}
                  placeholder="Your name"
                  style={{ width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Newsletter Checkbox Text
                </label>
                <input
                  type="text"
                  name="form_newsletter_text"
                  value={settings.form_newsletter_text}
                  onChange={handleInputChange}
                  placeholder="Sign up for our newsletter"
                  style={{ width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  Submit Button Text
                </label>
                <input
                  type="text"
                  name="form_submit_button"
                  value={settings.form_submit_button}
                  onChange={handleInputChange}
                  placeholder="Send"
                  style={{ width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transition: 'opacity 0.3s'
              }}
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>

          {/* Right Column - Preview */}
          <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '25px' }}>Widget Preview</h3>
            
            <div style={{ textAlign: 'center', color: '#999', padding: '40px 20px' }}>
              <p style={{ fontSize: '16px', lineHeight: '1.6' }}>Preview will show how the widget appears on your website</p>
              <p style={{ fontSize: '14px', marginTop: '15px', color: '#666' }}>Position: {settings.position}</p>
              
              {/* Mini Preview */}
              <div style={{ marginTop: '30px', display: 'inline-block', position: 'relative' }}>
                {settings.greeting_text && (
                  <div style={{
                    position: 'absolute',
                    bottom: '70px',
                    right: '70px',
                    background: 'white',
                    padding: '10px 16px',
                    borderRadius: '20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    fontSize: '13px',
                    whiteSpace: 'nowrap'
                  }}>
                    {settings.greeting_text}
                  </div>
                )}
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${settings.primary_color} 0%, ${settings.secondary_color} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '24px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  cursor: 'pointer'
                }}>
                  💬
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default WidgetSettings;
